import cuid from "cuid";
import { Formik, FieldArray } from "formik";
import { defaultsDeep, filter, find, get } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdArrowForwardIos,
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineCall,
  MdOutlineDelete,
  MdOutlineLocationOn,
  MdOutlineMail,
  MdPeople,
} from "react-icons/md";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM, DATE_FORMAT, RELATIONSHIP } from "utils/constants";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";

const RELATIONSHIPS = [
  { value: RELATIONSHIP.SPOUSE, label: "Conjoint(e)" },
  { value: RELATIONSHIP.CHILD, label: "Enfant" },
];

const getRecommendation = (membersCount) => {
  if (membersCount === 0)
    return { formula: "Solo", text: "Le prospect souhaite une couverture individuelle" };
  if (membersCount === 1)
    return { formula: "Duo", text: "Couverture pour 2 personnes du foyer" };
  return { formula: "Famille", text: `Couverture pour ${membersCount + 1} personnes du foyer` };
};

const Contact = ({ onNext, project }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const [nextLoading, setNextLoading] = useState(false);

  const initialValues = useMemo(() => defaultsDeep(
    {
      contact: { ...project.contact },
      members: (get(project, "contact.relatedPersons", []) || []).map((p) => ({
        id: p.id || cuid(),
        firstName: get(p, "identity.user.firstname", ""),
        lastName: get(p, "identity.user.lastname", ""),
        birthDate: get(p, "identity.user.birthDate", null),
        relationship: get(p, "relationship", ""),
      })),
      zipCode: find(get(project, "contact.telecoms", []), {
        system: CONTACT_POINT_SYSTEM.ZIP_CODE,
      }),
      phoneNumber: find(get(project, "contact.telecoms", []), {
        system: CONTACT_POINT_SYSTEM.PHONE,
      }),
    },
    {
      members: [],
      zipCode: { id: cuid(), system: CONTACT_POINT_SYSTEM.ZIP_CODE, value: null },
      phoneNumber: { id: cuid(), system: CONTACT_POINT_SYSTEM.PHONE, value: null },
      contact: {
        id: cuid(),
        user: { id: cuid(), firstname: null, lastname: null, birthDate: null },
      },
    },
  ), []);

  const onSubmit = (record) => {
    setNextLoading(true);
    const { members, zipCode, phoneNumber, contact } = record;
    const relatedPersons = members.map((m) => ({
      id: m.id,
      relationship: m.relationship,
      identity: {
        id: cuid(),
        user: {
          id: cuid(),
          firstname: m.firstName,
          lastname: m.lastName,
          birthDate: m.birthDate || null,
        },
      },
    }));

    const recommendation = getRecommendation(members.length);

    onNext({
      payload: {
        contact: {
          ...contact,
          user: contact.user,
          relatedPersons,
          telecoms: [
            ...get(contact, "telecoms", []).filter(
              ({ system }) =>
                ![CONTACT_POINT_SYSTEM.ZIP_CODE, CONTACT_POINT_SYSTEM.PHONE].includes(system),
            ),
            zipCode,
            phoneNumber,
          ],
        },
        visible: true,
        fields: {
          ...project.fields,
          recommendedFormula: recommendation.formula,
          householdSize: members.length + 1,
        },
      },
      onCompleted: () => setNextLoading(false),
      onError: () => setNextLoading(false),
    });
  };

  return (
    <>
      <Header key="header" title={DETAIL.TEMPLATES.CONTACT.TITLE} />
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {({ values, handleSubmit, setFieldValue, ...formProps }) => {
          const recommendation = getRecommendation(values.members.length);
          return (
            <div className="contact-form" key="form_area">
              <div className="contact-form__content">
                <Form
                  title="Qualification du Prospect"
                  description="Analyse de la composition du foyer pour recommander la meilleure formule"
                >
                  <div className="contact-stepper" key="ADHERENT">
                    <div className="contact-stepper__header">
                      <div className="contact-stepper__header--counter">
                        <span>1</span>
                      </div>
                      <div className="contact-stepper__header--content">
                        <h1 className="contact-stepper__header--content--title">
                          <span>Titulaire du contrat</span>
                        </h1>
                        <p className="contact-stepper__header--content--description">
                          Informations personnelles de l'adhérent principal
                        </p>
                      </div>
                    </div>
                    <div className="contact-stepper__content">
                      <Input
                        icon={<MdOutlineBadge size={22} />}
                        label="Nom"
                        placeholder="Ex : Durant"
                        {...bindInputProps({
                          name: "contact.user.lastname",
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        icon={<MdOutlineBadge size={22} />}
                        label="Prénom"
                        placeholder="Ex : Danielle"
                        {...bindInputProps({
                          name: "contact.user.firstname",
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        icon={<MdOutlineCake size={22} />}
                        label="Date de naissance"
                        placeholder="Ex : 01/01/2020"
                        type="date"
                        variant="simple"
                        format={DATE_FORMAT}
                        {...bindDateInputProps({
                          name: "contact.user.birthDate",
                          values,
                          setFieldValue,
                          ...formProps,
                        })}
                      />
                      <Input
                        icon={<MdOutlineLocationOn size={22} />}
                        label="Code postal"
                        placeholder="Ex : 69001"
                        {...bindInputProps({
                          name: "zipCode.value",
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        icon={<MdOutlineCall size={16} />}
                        label="Téléphone"
                        placeholder="Ex : 06 12 34 56 78"
                        type="phone"
                        inputMode="numeric"
                        maxLength={10}
                        {...bindInputProps({
                          name: "phoneNumber.value",
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                  </div>

                  <div className="contact-stepper" key="MEMBERS">
                    <div className="contact-stepper__header">
                      <div className="contact-stepper__header--counter">
                        <span>2</span>
                      </div>
                      <div className="contact-stepper__header--content">
                        <h1 className="contact-stepper__header--content--title">
                          <span>Composition du foyer</span>
                        </h1>
                        <p className="contact-stepper__header--content--description">
                          Ajoutez les membres du foyer pour déterminer la formule optimale
                        </p>
                      </div>
                    </div>
                    <div className="contact-stepper__content">
                      <FieldArray
                        name="members"
                        render={(arrayHelpers) => (
                          <>
                            {values.members.map((member, i) => (
                              <div className="contact-stepper__content--box" key={`member-${member.id}`}>
                                <div className="contact-stepper__content--box--header">
                                  <h1 className="contact-stepper__content--box--header--title">
                                    Membre {i + 1}
                                  </h1>
                                  <div
                                    className="contact-stepper__content--box--header--action"
                                    onClick={() => arrayHelpers.remove(i)}
                                  >
                                    <MdOutlineDelete size={14} />
                                    <span>Supprimer</span>
                                  </div>
                                </div>
                                <Input
                                  icon={<MdOutlineBadge size={22} />}
                                  label="Prénom"
                                  placeholder="Prénom"
                                  {...bindInputProps({
                                    name: `members.${i}.firstName`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                                <Input
                                  icon={<MdOutlineBadge size={22} />}
                                  label="Nom"
                                  placeholder="Nom"
                                  {...bindInputProps({
                                    name: `members.${i}.lastName`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                                <Input
                                  icon={<MdOutlineCake size={22} />}
                                  label="Date de naissance"
                                  type="date"
                                  variant="simple"
                                  format={DATE_FORMAT}
                                  {...bindDateInputProps({
                                    name: `members.${i}.birthDate`,
                                    values,
                                    setFieldValue,
                                    ...formProps,
                                  })}
                                />
                                <Select
                                  label="Lien de parenté"
                                  placeholder="Sélectionner"
                                  options={RELATIONSHIPS}
                                  {...bindInputProps({
                                    name: `members.${i}.relationship`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                            ))}
                            <button
                              key="add-member"
                              className="button__action"
                              onClick={() =>
                                arrayHelpers.push({
                                  id: cuid(),
                                  firstName: "",
                                  lastName: "",
                                  birthDate: null,
                                  relationship: "",
                                })
                              }
                            >
                              <MdAdd size={22} />
                              Ajouter un membre
                            </button>
                          </>
                        )}
                      />
                    </div>
                  </div>

                  <div className="quote quote--info">
                    <MdPeople className="quote--icon" size={36} />
                    <p className="quote--description">
                      <strong>Formule {recommendation.formula} recommandée</strong>
                      {" - "}{recommendation.text}
                    </p>
                  </div>
                </Form>
              </div>
              <div className="contact-form__action">
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={nextLoading}
                  disabled={nextLoading}
                >
                  Continuer
                  <MdArrowForwardIos size={20} />
                </Button>
              </div>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default Contact;
